import { Injectable } from '@angular/core';
import { gql } from '@apollo/client/core';
import { Observable } from 'rxjs';
import { GraphQLService } from './graphql.service';
import { environment } from '../../environments/environment';
import { DataService } from '../shared/services/data/data.service';
import { IntegrationSourceService } from '../shared/services/source-service-api/integration-source.service';

// Define types based on your GraphQL schema
export interface Source {
  id: string;
  name: string;
  type: string;
  status: string;
  lastSyncTime?: string;
  nextSyncTime?: string;
}

export interface SourcesResponse {
  sources: {
    items: Source[];
    totalCount: number;
  };
}

export interface SourceResponse {
  source: Source;
}

export interface SourceInput {
  name: string;
  type: string;
  config: any;
}

export interface InitReAuthResponse {
  [key: string]: any;
}

// GraphQL queries and mutations
const GET_SOURCES = gql`
  query getSources($limit: Int, $offset: Int) {
    sources(limit: $limit, offset: $offset) {
      items {
        id
        name
        type
        status
        lastSyncTime
        nextSyncTime
      }
      totalCount
    }
  }
`;

const GET_SOURCE = gql`
  query getSource($id: ID!) {
    source(id: $id) {
      id
      name
      type
      status
      lastSyncTime
      nextSyncTime
    }
  }
`;

const CREATE_SOURCE = gql`
  mutation createSource($input: SourceInput!) {
    createSource(input: $input) {
      id
      name
      type
      status
    }
  }
`;

const UPDATE_SOURCE = gql`
  mutation updateSource($id: ID!, $input: SourceInput!) {
    updateSource(id: $id, input: $input) {
      id
      name
      type
      status
    }
  }
`;

const DELETE_SOURCE = gql`
  mutation deleteSource($id: ID!) {
    deleteSource(id: $id) {
      success
      message
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class SourceGraphQLService {
  private apiUrlSourceService = environment.apiUrlSourceService;

  constructor(
    private graphQLService: GraphQLService,
    private dataService: DataService,
    private sourceServiceApi: IntegrationSourceService
  ) {}

  /**
   * Get list of sources with pagination
   */
  getSources(limit: number = 10, offset: number = 0): Observable<SourcesResponse> {
    return this.graphQLService.query<SourcesResponse>(GET_SOURCES, { limit, offset });
  }

  /**
   * Get a single source by ID
   */
  getSource(id: string): Observable<SourceResponse> {
    return this.graphQLService.query<SourceResponse>(GET_SOURCE, { id });
  }

  /**
   * Get sources with authentication
   */
  getSourcesWithAuth(limit: number = 10, offset: number = 0): Observable<SourcesResponse> {
    return this.graphQLService.queryWithAuth<SourcesResponse>(GET_SOURCES, { limit, offset });
  }

  /**
   * Create a new source
   */
  createSource(input: SourceInput): Observable<{ createSource: Source }> {
    return this.graphQLService.mutate<{ createSource: Source }>(CREATE_SOURCE, { input });
  }

  /**
   * Update an existing source
   */
  updateSource(id: string, input: SourceInput): Observable<{ updateSource: Source }> {
    return this.graphQLService.mutate<{ updateSource: Source }>(UPDATE_SOURCE, { id, input });
  }

  /**
   * Delete a source
   */
  deleteSource(id: string): Observable<{ deleteSource: { success: boolean, message: string } }> {
    return this.graphQLService.mutate<{ deleteSource: { success: boolean, message: string } }>(DELETE_SOURCE, { id });
  }

  /**
   * Create source with authentication
   */
  createSourceWithAuth(input: SourceInput): Observable<{ createSource: Source }> {
    return this.graphQLService.mutateWithAuth<{ createSource: Source }>(CREATE_SOURCE, { input });
  }

  /**
   * Initialize re-authentication flow for an integration
   * @param integrationId The integration ID (path parameter)
   * @param userId The user ID (query parameter)
   * @param dataSourceType The data source type (query parameter)
   * @param productId The product ID (query parameter)
   * @returns Observable of InitReAuthResponse
   */
  initReAuthIntegration(
    integrationId: number,
    userId: number,
    dataSourceType: string = 'XERO_V2',
    productId: number = 1
  ): Observable<InitReAuthResponse> {
    return this.sourceServiceApi.initReAuth(integrationId, userId, dataSourceType, productId) as Observable<InitReAuthResponse>;
  }

  /**
   * Re-authenticate integration via connector studio
   * @param integrationId The integration ID (path parameter)
   * @param userId The user ID (query parameter)
   * @param productId The product ID (query parameter)
   * @param additionalProps Additional properties for re-authentication
   * @returns Observable of InitReAuthResponse
   */
  reAuthIntegrationConnectorStudio(
    integrationId: number,
    userId?: number,
    productId?: number,
    additionalProps?: {
      [key: string]: any;
    }
  ): Observable<InitReAuthResponse> {
    return this.sourceServiceApi.reAuthConnectorStudio(
      integrationId,
      userId,
      additionalProps?.sessionState,
      additionalProps?.eqp,
      productId
    ) as Observable<InitReAuthResponse>;
  }

  /**
   * Sync integrations
   * @param integrationIds Array of integration IDs to sync
   * @param initiatedBy User ID who initiated the sync
   * @param productId The product ID (query parameter)
   * @returns Observable of sync response
   */
  syncIntegrations(
    integrationIds: number[],
    initiatedBy: number,
    productId: number = 1
  ): Observable<any> {
    return this.sourceServiceApi.syncIntegrations(integrationIds, initiatedBy, productId);
  }

  /**
   * Sync specific tables for integrations
   * @param tableName Name of the table to sync
   * @param integrationIds Array of integration IDs to sync
   * @param initiatedBy User ID who initiated the sync (0 for system-initiated)
   * @param productId The product ID (query parameter)
   * @returns Observable of sync response
   */
  syncTables(
    tableName: string,
    integrationIds: number[],
    initiatedBy: number = 0,
    productId: number = 1
  ): Observable<any> {
    return this.sourceServiceApi.syncTables(tableName, integrationIds, initiatedBy, productId);
  }
}
